import type { ReactNode, ErrorInfo } from 'react';
import { Component } from 'react';
import { trackClientError } from '../../utils/analyticsEvents';
import { createComponentStackHash } from '../../utils/hash';
import styles from './ErrorBoundary.module.scss';
import { UpdateApplication } from '../UpdateApplication/UpdateApplication';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  reload: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      reload: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (error?.message?.includes('Failed to fetch dynamically imported module')) {
      this.setState({ reload: true });
      return;
    }
    const stack = errorInfo?.componentStack ?? '';
    // create a compact hash to avoid sending raw stack
    const hash = createComponentStackHash(stack);
    trackClientError(error.message, hash);

    this.setState({
      error,
      errorInfo,
    });
  }

  render(): ReactNode {
    const { error, errorInfo, reload } = this.state;
    const { children } = this.props;

    if (reload) {
      return <UpdateApplication />;
    }

    if (error || errorInfo) {
      return (
        <div>
          <h2 className={styles.title}>Something went wrong.</h2>
          <details className={styles.detailsWrapper}>
            {error && error.toString()}
            <br />
            {errorInfo?.componentStack}
          </details>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
